import React, {FC} from "react";
import {QuantityTypeTemplateQuestion} from "../../../lib/src/api/dto/QuantityTypeTemplateQuestion"
import {faChevronDown, faChevronUp, faTimes} from "@fortawesome/free-solid-svg-icons";
import { Input } from "@traas/lib/src/components/form/Input";
import { IconButton } from "@traas/lib/src/components/form/Button";
import { Toggle } from "@traas/lib/src/components/form/Toggle";
import { Select } from "@traas/lib/src/components/form/Select";
import { PillSelect } from "@traas/lib/src/components/form/PillSelect";

export const TaskTypeTemplatesQuestionEditor: FC<{n: number, question: QuantityTypeTemplateQuestion, updateQuestion: (value: Partial<Omit<QuantityTypeTemplateQuestion,"id">>) => void, deleteQuestion: () => void}> = props => {
  const setName = (value: string)=> props.updateQuestion({question: value})
  const setType = (value: string)=> {
    if (value !== 'value') {
      props.updateQuestion({min: null})
      props.updateQuestion({max: null})
      props.updateQuestion({export_multiplier: 1})
      props.updateQuestion({export_decimals: 1})
      props.updateQuestion({export_unit: null})
    }
    props.updateQuestion({type: value as QuantityTypeTemplateQuestion['type']})
  }
  const setShouldExport = (value: boolean)=> props.updateQuestion({should_export: value})
  const setExportName = (value: string|null) => props.updateQuestion({exportName: value})

  // Value specific
  const setMin = (value: number|null) => props.updateQuestion({min: value})
  const setMax = (value: number|null) => props.updateQuestion({max: value})
  const setExportMultiplier = (value: number|null) => props.updateQuestion({export_multiplier: value})
  const setExportDecimals = (value: number|null) => props.updateQuestion({export_decimals: value})
  const setExportUnit = (value: string|null) => props.updateQuestion({export_unit: value})

  // Multi specific
  const setOptions = (value: string[]) => props.updateQuestion({options: value})
  const setIncludeOtherOption = (value: boolean|null) => props.updateQuestion({includeOtherOption: value})
  const setPictureRequirements = (value: QuantityTypeTemplateQuestion['pictureRequirements']) => props.updateQuestion({pictureRequirements: value})

  return <div className={'bg-white shadow-xs rounded-lg border border-slate-200 flex items-stretch'}>
    <div className={'flex flex-col items-center border-r border-slate-200 w-10 py-2'}>
      <IconButton type={'secondary'} size={'sm'} icon={faChevronUp} onClick={() => {
        props.updateQuestion({order: props.question.order - 1.5})
      }} />
      <IconButton type={'secondary'} size={'sm'} icon={faChevronDown} onClick={() => {
        props.updateQuestion({order: props.question.order + 1.5})
      }} />
    </div>
    <div className={'flex flex-col items-center justify-center w-10 h-10 mt-2'}>
      <div className={'h-6 w-6 bg-blue-800 text-white font-medium flex items-center justify-center rounded-full text-sm'}>{props.question.order}</div>
    </div>
    <div className={'flex-1 pr-3 py-2'}>
      <div className={'grid grid-cols-6 gap-2'}>
        <div className={'-mt-1 mr-2 col-span-2'}>
          <Select label={''} value={props.question.type} onChange={setType} options={{
            'boolean': 'Ja/Nee',
            'text': 'Open',
            'value': 'Waarde',
            'multi': 'Meerkeuze',
          }} />
        </div>
        <div className={'-mt-1 mr-2 col-span-3'}>
          <Input type={'text'} label={''} value={props.question.question} onChange={setName} />
        </div>
        <div className={'flex items-center justify-end'}>
          <IconButton size={'md'} type={'danger'} icon={faTimes} onClick={() => {
            if (confirm('Wil je deze vraag verwijderen?')) {
              props.deleteQuestion()
            }
          }} />
        </div>
      </div>
      <h3 className={'mt-4 text-sm font-medium'}>Opties</h3>

      {/* Export options */}
      <div className={'grid grid-cols-6 gap-2 mt-3'}>
        <div className={'col-span-3'}>
          <Toggle label={'Antwoord in rapport?'} size={'sm'} value={props.question.should_export} onChange={setShouldExport} />
        </div>
      </div>
      <div className={'grid grid-cols-6 gap-2 mt-3'}>
        <div className={'col-span-3'}>
          <Toggle label={'Alternatieve titel in rapport?'} size={'sm'} value={props.question.exportName !== null} onChange={(v) => setExportName(v ? props.question.question : null)} />
        </div>
        {props.question.exportName !== null && <div className={'-mt-1 mr-2 col-span-3'}>
          <Input type={'text'} label={''} value={props.question.exportName ?? props.question.question} onChange={setExportName}/>
        </div>}
      </div>

      {/* Multi specific fields */}
      {props.question.type === 'multi' && <>
        <div className={'grid grid-cols-6 gap-2 mt-3'}>
          <div className={'col-span-3'}>
            <Input type={'textarea'} label={'Opties (scheid met enter)'} value={props.question.options.join('\n')} onChange={(v) => setOptions(v.split('\n'))} />
          </div>
        </div>
        <div className={'grid grid-cols-6 gap-2 mt-3'}>
          <div className={'col-span-3'}>
            <Toggle label={'Optie "Anders, ..." met open-veld'} size={'sm'} value={props.question.includeOtherOption ?? false} onChange={setIncludeOtherOption} />
          </div>
        </div>
      </>}

      {/* Value specific fields */}
      {props.question.type === 'value' && <>
        <div className={'grid grid-cols-6 gap-2 mt-3'}>
          <div className={'col-span-2'}><Input type={'number'} label={'Min'} value={props.question.min ?? 0} onChange={setMin} /></div>
          <div className={'col-span-2'}><Input type={'number'} label={'Max'} value={props.question.max ?? 0} onChange={setMax} /></div>
          <div className={'col-span-2'}><Input type={'number'} label={'Vermenigvuldigingsfactor in rapport'} value={props.question.export_multiplier ?? 1} onChange={setExportMultiplier} /></div>
          <div className={'col-span-2'}><Input type={'number'} label={'Decimalen in rapport'} value={props.question.export_decimals ?? 0} onChange={setExportDecimals} min={0} /></div>
          <div className={'col-span-2'}><Input type={'text'} label={'Eenheid in rapport'} value={props.question.export_unit ?? ''} onChange={setExportUnit} /></div>
        </div>
      </>}

      <h3 className={'mt-4 text-sm font-medium'}>Foto opties</h3>
      {props.question.type === 'boolean' ? <>
        <table>
          <tbody>
            <tr>
              <td>Bij <strong>Ja</strong></td><td className={"py-2 px-4"}><FotoOptions value={props.question.pictureRequirements.find(r => r.if_value === true) ? (props.question.pictureRequirements.find(r => r.if_value === true)?.is_required ? "required" : "optional") : "none"} onChange={(val) => {
                const requirements = [
                  // Take all requirements, except for the value=true requirements, this/these will be overwritten.
                  ...props.question.pictureRequirements.filter(v => v.if_value !== true),
                ]
                if (val === 'required') {
                  requirements.push({if_value: true, is_required: true})
                } else if (val === 'optional') {
                  requirements.push({if_value: true, is_required: false})
                }
                setPictureRequirements(requirements)
              }} /></td>
            </tr>
            <tr>
              <td>Bij <strong>Nee</strong></td><td className={"py-2 px-4"}><FotoOptions value={props.question.pictureRequirements.find(r => r.if_value === false) ? (props.question.pictureRequirements.find(r => r.if_value === false)?.is_required ? "required" : "optional") : "none"} onChange={(val) => {
                const requirements = [
                  // Take all requirements, except for the value=true requirements, this/these will be overwritten.
                  ...props.question.pictureRequirements.filter(v => v.if_value !== false),
                ]
                if (val === 'required') {
                  requirements.push({if_value: false, is_required: true})
                } else if (val === 'optional') {
                  requirements.push({if_value: false, is_required: false})
                }
                setPictureRequirements(requirements)
              }} /></td>
            </tr>
          </tbody>
        </table>
      </> : <div className={'mt-6 mb-6'}>
        <FotoOptions value={props.question.pictureRequirements.find(r => r.if_value === null) ? (props.question.pictureRequirements.find(r => r.if_value === null)?.is_required ? "required" : "optional") : "none"} onChange={(val) => {
          const requirements = [
            // Take all requirements, except for the value=true requirements, this/these will be overwritten.
            ...props.question.pictureRequirements.filter(v => v.if_value !== null),
          ]
          if (val === 'required') {
            requirements.push({if_value: null, is_required: true})
          } else if (val === 'optional') {
            requirements.push({if_value: null, is_required: false})
          }
          setPictureRequirements(requirements)
        }}  />
      </div>}
    </div>
  </div>
}

const FotoOptions: FC<{value: "none"|"optional"|"required", onChange: (val: "none"|"optional"|"required") => void}> = (props) => {
  return <div className={'-mt-4 -mb-6'}>
    <PillSelect options={{
      "none": "Geen Foto",
      "optional": "Optioneel",
      "required": "Verplicht",
    }} value={props.value} onChange={(v) => props.onChange(v as "none"|"optional"|"required")} />
  </div>
}